import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Scale, FileText, Cpu, Landmark, CheckCircle2, Search } from "lucide-react";

const QUESTIONS = [
  "How much is the municipality paying?",
  "What services were purchased?",
  "What is actually being used?",
  "What are the ongoing annual costs?",
  "Were projects delivered on time and budget?",
  "What measurable benefit did taxpayers receive?",
  "Were Canadian suppliers or alternatives considered?",
];

const CATEGORIES = [
  {
    icon: Cpu,
    title: "Municipal technology spending & Oracle research",
    text: "Enterprise software licensing, cloud contracts and ERP systems (including Oracle products) are among the largest recurring technology costs a municipality carries. This research category collects public records on what was purchased, what it costs each year, and what is measurably in use — factual questions only, no unsupported claims.",
  },
  {
    icon: FileText,
    title: "Procurement & contracts",
    text: "Tenders, sole-source awards, change orders and vendor confidentiality — what the public record shows about how buying decisions were made.",
  },
  {
    icon: Landmark,
    title: "Capital projects",
    text: "Budgets versus actuals, timelines and delivered outcomes for roads, facilities and infrastructure.",
  },
];

export default function Accountability() {
  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 mb-4">
          <Scale className="h-3 w-3 mr-1" /> Taxpayer Watch
        </Badge>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="accountability-title">
          What is your municipality paying for?
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          Explore public records, technology contracts, software spending, procurement and other municipal costs —
          and ask whether taxpayers are getting value for their money. Everything here is framed around factual,
          sourced questions. We never publish unsupported claims.
        </p>
        <div className="flex flex-wrap gap-3 mt-6">
          <Button asChild data-testid="accountability-investigate-button">
            <Link to="/accountability/investigate">Investigate My Municipality</Link>
          </Button>
          <Button asChild variant="outline"><Link to="/news">Read investigations in the Newsroom</Link></Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8 mt-12 items-start">
        <Card>
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-heading font-semibold text-lg text-foreground mb-4 flex items-center gap-2">
              <Search className="h-5 w-5 text-accent" /> The questions we ask
            </h2>
            <ul className="space-y-2.5">
              {QUESTIONS.map((q) => (
                <li key={q} className="flex items-start gap-2.5 text-sm text-foreground">
                  <CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" /> {q}
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground leading-relaxed mt-5">
              Answers come from public records: budgets, financial statements, council reports, procurement documents
              and freedom-of-information responses. Where records are confidential, we say so — confidentiality is
              lawful and common, and it is never treated as proof of wrongdoing.
            </p>
          </CardContent>
        </Card>
        <div className="space-y-4">
          <h2 className="font-heading font-semibold text-lg text-foreground">Research categories</h2>
          {CATEGORIES.map((c) => (
            <Card key={c.title} data-testid={`accountability-category-${c.title.slice(0, 10).toLowerCase().replace(/\s+/g, "-")}`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-3.5">
                  <div className="h-10 w-10 rounded-lg bg-secondary flex items-center justify-center shrink-0">
                    <c.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-sm text-foreground mb-1.5">{c.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{c.text}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      <Card className="mt-10 bg-secondary max-w-3xl">
        <CardContent className="p-6">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Municipal accountability research is separate from the Witness Your Vote declaration system. It never uses
            voter data, and findings are always presented as sourced facts and open questions — not conclusions.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
